import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import he from "he";
import image_logo from "../../assets/img/general/1assd.png";
import "../../assets/css/Header.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Header() {
  const [allRecords, setAllRecords] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobileMenus, setExpandedMobileMenus] = useState({});
  const location = useLocation();

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const toggleMobileSubmenu = (menuKey) => {
    setExpandedMobileMenus((previous) => ({
      ...previous,
      [menuKey]: !previous[menuKey],
    }));
  };

  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(clockInterval);
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchMenuData() {
      try {
        await axios.get("sanctum/csrf-cookie");

        const response = await axios.get(
          `${BASE_URL}pagdatab?itemName[eq]=menue_tab&status02[eq]=publish`,
        );

        if (isMounted && response.data.status === 200) {
          setAllRecords(response.data.allData || []);
        }
      } catch (error) {
        console.error("Error loading ministry navigation:", error);
      }
    }

    fetchMenuData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  // Keep the desktop navigation fixed at the top after it scrolls into view.
  // On mobile, the same navigation remains a slide-out drawer.
  useEffect(() => {
    const navigation = document.getElementById("fmw-main-navigation");
    if (!navigation) return undefined;

    const desktopQuery = window.matchMedia("(min-width: 992px)");
    const placeholder = document.createElement("div");
    placeholder.setAttribute("aria-hidden", "true");
    placeholder.style.display = "none";
    navigation.parentNode.insertBefore(placeholder, navigation);

    let originalTop = navigation.getBoundingClientRect().top + window.scrollY;

    const removeFixedState = () => {
      navigation.classList.remove("fmw-navigation-fixed");
      placeholder.style.display = "none";
      placeholder.style.height = "0px";
    };

    const updateNavigation = () => {
      if (!desktopQuery.matches) {
        removeFixedState();
        return;
      }

      if (window.scrollY >= originalTop) {
        placeholder.style.display = "block";
        placeholder.style.height = `${navigation.offsetHeight}px`;
        navigation.classList.add("fmw-navigation-fixed");
      } else {
        removeFixedState();
      }
    };

    const updatePosition = () => {
      if (!navigation.classList.contains("fmw-navigation-fixed")) {
        originalTop = navigation.getBoundingClientRect().top + window.scrollY;
      }
      updateNavigation();
    };

    window.addEventListener("scroll", updateNavigation, { passive: true });
    window.addEventListener("resize", updatePosition);
    desktopQuery.addEventListener?.("change", updatePosition);
    updateNavigation();

    return () => {
      window.removeEventListener("scroll", updateNavigation);
      window.removeEventListener("resize", updatePosition);
      desktopQuery.removeEventListener?.("change", updatePosition);
      placeholder.remove();
      navigation.classList.remove("fmw-navigation-fixed");
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") closeMobileMenu();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileMenuOpen]);

  const decodeLabel = (value = "") => he.decode(String(value));

  const getItemLink = (item, level = "top") => {
    if (item.linker) return item.linker;

    const label = decodeLabel(item.item03).replace(/ & /g, "_");

    if (level === "grandchild") {
      return `/${label}/${item.item02b}`;
    }

    if (level === "child") {
      return `/${label}/${item.item02}`;
    }

    return `/${label}/${item.item01}`;
  };

  const getGrandSubMenu = (
    records,
    parentValue,
    childValue,
    item02bValue = 0,
  ) =>
    records
      .filter(
        (item) =>
          String(item.item01) === String(parentValue) &&
          Number(item.item02) === Number(childValue) &&
          Number(item.item02b) > Number(item02bValue),
      )
      .map((item, index) => (
        <li key={`grand-${parentValue}-${childValue}-${index}`}>
          <Link
            to={getItemLink(item, "grandchild")}
            target={item.triger || undefined}
            onClick={closeMobileMenu}
          >
            {decodeLabel(item.item03)}
          </Link>
        </li>
      ));

  const getSubMenu = (
    records,
    parentValue,
    item02Value = 0,
    item02bValue = 0,
  ) =>
    records
      .filter(
        (item) =>
          String(item.item01) === String(parentValue) &&
          Number(item.item02) > Number(item02Value) &&
          Number(item.item02b) === Number(item02bValue),
      )
      .map((item, index) => {
        const hasGrandChildren = records.some(
          (record) =>
            String(record.item01) === String(parentValue) &&
            Number(record.item02) === Number(item.item02) &&
            Number(record.item02b) > Number(item02bValue),
        );

        return (
          <li
            key={`child-${parentValue}-${item.item02}-${index}`}
            className={hasGrandChildren ? "dropdown" : undefined}
          >
            <Link
              to={getItemLink(item, "child")}
              target={item.triger || undefined}
              onClick={closeMobileMenu}
            >
              <span>{decodeLabel(item.item03)}</span>
            </Link>

            {hasGrandChildren && (
              <ul>{getGrandSubMenu(records, parentValue, item.item02)}</ul>
            )}
          </li>
        );
      });

  const topLevelItems = allRecords.filter(
    (item) => Number(item.item02) === 0 && Number(item.item02b) === 0,
  );

  return (
    <div className="fmw-site-header">
      {/* Official government strip */}
      <div className="fmw-government-bar">
        <div className="container fmw-government-inner">
          <div className="fmw-top-left">
            <div className="fmw-government-message">
              <span className="fmw-government-dot" aria-hidden="true" />
              <span>Follow Us On:</span>
            </div>

            <div className="fmw-top-social-links">
              <a
                href="https://x.com/FMWNIG"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit FMW on X"
                title="X (Twitter)"
              >
                <i className="fa-brands fa-x-twitter" aria-hidden="true" />
              </a>

              <a
                href="https://www.facebook.com/FMWNG"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit FMW on Facebook"
                title="Facebook"
              >
                <i className="fa-brands fa-facebook-f" aria-hidden="true" />
              </a>

              <a
                href="https://www.linkedin.com/in/FMWNG"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit FMW on LinkedIn"
                title="LinkedIn"
              >
                <i className="fa-brands fa-linkedin-in" aria-hidden="true" />
              </a>

              <a
                href="https://www.instagram.com/fmwng"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit FMW on Instagram"
                title="Instagram"
              >
                <i className="fa-brands fa-instagram" aria-hidden="true" />
              </a>

              <a
                href="https://www.youtube.com/channel/UCu9KmHLhSyhPItgK6yGo45Q"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit FMW on YouTube"
                title="YouTube"
              >
                <i className="fa-brands fa-youtube" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="fmw-top-contact">
            <a href="mailto:info@fmw.gov.ng">
              <i className="fa-regular fa-envelope" aria-hidden="true" />
              <span>info@fmw.gov.ng</span>
            </a>

            <a href="tel:+2348061743313">
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>+234 9 122 0581 61</span>
            </a>
          </div>
        </div>
      </div>

      {/* Ministry identity */}
      <header className="fmw-branding">
        <div className="container fmw-branding-inner">
          <Link
            to="/"
            className="fmw-brand"
            aria-label="Federal Ministry of Works homepage"
          >
            <img
              src={image_logo}
              alt="Ministry emblem"
              className="fmw-brand-logo"
              loading="eager"
              decoding="async"
            />

            <span className="fmw-brand-divider" aria-hidden="true" />

            <span className="fmw-brand-copy">
              <span className="fmhud-brand-kicker">Federal Republic of Nigeria</span>
              <span className="fmw-brand-title">FEDERAL MINISTRY OF WORKS</span>
              <span className="fmw-brand-country">
                ... building the backbone for Development ...
              </span>
            </span>
          </Link>

          <div className="fmw-brand-side fmw-live-clock">
            <span className="fmw-clock-label">
              <i className="fa-regular fa-clock" aria-hidden="true"></i>
              NIGERIA STANDARD TIME
            </span>

            <time className="fmw-clock-time">
              {currentDateTime.toLocaleTimeString("en-NG", {
                timeZone: "Africa/Lagos",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })}
              <span className="fmw-clock-zone"> WAT</span>
            </time>

            <time
              className="fmw-clock-date"
              dateTime={currentDateTime.toLocaleDateString("en-CA", {
                timeZone: "Africa/Lagos",
              })}
            >
              {currentDateTime.toLocaleDateString("en-NG", {
                timeZone: "Africa/Lagos",
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </time>
          </div>

          <button
            type="button"
            className="fmw-mobile-toggle"
            onClick={() => setMobileMenuOpen((previous) => !previous)}
            aria-label={
              mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={mobileMenuOpen}
            aria-controls="fmw-main-navigation"
          >
            <i
              className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"}`}
              aria-hidden="true"
            />
            {/* <span>{mobileMenuOpen ? "Close" : "Menu"}</span> */}
          </button>
        </div>
      </header>

      {/* Primary navigation */}
      <nav
        id="fmw-main-navigation"
        className={`fmw-navigation ${
          mobileMenuOpen ? "fmw-navigation-open" : ""
        }`}
        aria-label="Main navigation"
      >
        <div className="container fmw-navigation-inner">
          <ul className="fmw-nav-list">
            {topLevelItems.map((item, index) => {
              const hasChildren = allRecords.some(
                (record) =>
                  String(record.item01) === String(item.item01) &&
                  Number(record.item02) > 0,
              );

              const menuLabel = decodeLabel(item.item03);

              return (
                <li
                  key={`top-${item.item01}-${index}`}
                  className={[
                    hasChildren ? "fmw-has-dropdown" : "",
                    expandedMobileMenus[`top-${item.item01}`]
                      ? "fmw-mobile-expanded"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="fmw-mobile-menu-row">
                    <Link
                      to={getItemLink(item)}
                      target={item.triger || undefined}
                      onClick={closeMobileMenu}
                      aria-haspopup={hasChildren ? "true" : undefined}
                    >
                      <span>{menuLabel}</span>
                    </Link>

                    {hasChildren && (
                      <button
                        type="button"
                        className="fmw-mobile-submenu-toggle"
                        aria-label={`Toggle ${menuLabel} submenu`}
                        aria-expanded={
                          !!expandedMobileMenus[`top-${item.item01}`]
                        }
                        onClick={() =>
                          toggleMobileSubmenu(`top-${item.item01}`)
                        }
                      >
                        <i
                          className="fa-solid fa-chevron-down"
                          aria-hidden="true"
                        />
                      </button>
                    )}
                  </div>

                  {hasChildren && (
                    <ul className="fmw-dropdown-menu">
                      {getSubMenu(allRecords, item.item01)}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Mobile navigation backdrop */}
      {mobileMenuOpen && (
        <button
          type="button"
          className="fmw-menu-backdrop"
          onClick={closeMobileMenu}
          aria-label="Close navigation menu"
          tabIndex={-1}
        />
      )}
    </div>
  );
}

export default Header;
